import Button from '@components/v2/ui/Button/Button';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import { ElementType, ReactNode } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';

type TranslationValues = Record<string, any>;

interface EmptyStateTitle {
  text: LangEnum;
  values?: TranslationValues;
  as?: ElementType;
}

interface EmptyStateDescription {
  text: LangEnum;
  values?: TranslationValues;
}

interface EmptyStateActionButton {
  label: LangEnum;
  labelValues?: TranslationValues;
  variant?: 'contained' | 'outlined';
  onClick: () => void;
}

interface EmptyStateAction {
  primary?: EmptyStateActionButton;
  secondary?: EmptyStateActionButton;
}

interface EmptyStateProps {
  title: EmptyStateTitle;
  description?: EmptyStateDescription;
  action?: EmptyStateAction;
  illustration?: ReactNode;
  testId?: string;
}

const STACK_GAP = 1.25;
const CONTENT_MAX_WIDTH = 320;

function EmptyState({ title, description, action, illustration, testId }: EmptyStateProps) {
  const { translate } = useTranslation();

  const primaryAction = action?.primary;
  const secondaryAction = action?.secondary;
  const hasActions = Boolean(primaryAction || secondaryAction);

  return (
    <Stack
      role="status"
      test-id={testId}
      sx={{ alignItems: 'flex-start', gap: STACK_GAP, maxWidth: CONTENT_MAX_WIDTH }}
    >
      <Stack sx={{ alignItems: 'flex-start', gap: STACK_GAP, pb: hasActions ? 1 : 0 }}>
        {illustration && <Box aria-hidden>{illustration}</Box>}
        <Stack sx={{ alignItems: 'flex-start', gap: STACK_GAP }}>
          <Typography component={title.as ?? 'h2'} variant="h5" color="text.primary">
            {translate(title.text, title.values)}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {translate(description.text, description.values)}
            </Typography>
          )}
        </Stack>
      </Stack>
      {hasActions && (
        <Stack direction="row" sx={{ gap: 1 }}>
          {primaryAction && (
            <Button variant="contained" color="primary" onClick={primaryAction.onClick}>
              {translate(primaryAction.label, primaryAction.labelValues)}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="contained" color="secondary" onClick={secondaryAction.onClick}>
              {translate(secondaryAction.label, secondaryAction.labelValues)}
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}

EmptyState.displayName = 'EmptyState';
export type {
  EmptyStateProps,
  EmptyStateTitle,
  EmptyStateDescription,
  EmptyStateAction,
  EmptyStateActionButton,
};
export default EmptyState;
